import React, {
    useCallback, useEffect, useMemo, useState
} from 'react'
import { useDispatch } from 'react-redux'
import styled from 'styled-components'
import { Link, useHistory } from 'react-router-dom'
import { filter } from 'lodash'
import placeholderImg from 'assets/mugshot-placeholder.png'
import { _, colors } from 'utils'
import { useDeleteItem, useEventSlug } from 'hooks'
import { CardWrapper, NotFound } from 'components'
import Icon from 'components/Icon'
import routes from 'routes'
import { Filter, SearchInput, TopToolbar } from 'components/lists'
import DataGrid, { ColumnDataGrid, DataGridProps } from 'components/DataGrid'
import {
    PerformerOrder,
    PerformerType
} from 'generated/graphql'
import { RequestStatus } from 'types'
import { Pagination } from '../pagination'
import { deleteSpeaker } from './graphql/mutation'
import { useSpeakers } from './hooks'
import { handleSubmit, importProps } from './importConfig'

const S = {
    SearchInput: styled(SearchInput)`
        margin: 8px 0 !important;
    `,
    InfoWrapper: styled.div`
        display: flex;
        align-items: center;
        width: 100%;
        min-width: 0;
        max-width: 900px;
        overflow: hidden;
    `,
    Avatar: styled.div`
        background: #ffffff;
        border-radius: 50%;
        height: 40px;
        width: 40px;
        min-width: 40px;
        margin-right: 10px;
        display: flex;
        align-items: center;
        justify-content: center;
        overflow: hidden;
    `,
    Thumbnail: styled.img`
        height: 40px;
        width: 40px;
    `,
    Title: styled.div`
        height: 40px;
        display: flex;
        flex-direction: column;
        justify-content: center;
        overflow: hidden;
    `,
    Name: styled.div`
        font-weight: 500;
        font-size: 14px;
        line-height: 16px;
        color: ${colors.grayTitle};
    `,
    About: styled.div`
        height: 16px;
        margin-top: 6px;
        color: ${colors.grayDescription};
        font-size: 14px;
        line-height: 16px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        > {
            * {
                display: inline;
            }
            br {
                display: none;
            }
        }
    `,
    Action: styled.div`
        width: 100%;
        text-align: right;
    `,
    Filter: styled(Filter)`
        width: 197px;
        margin-left: 20px;
    `,
    Image: styled.div<{ image: string }>`
        width: 56px;
        height: 40px;
        background: ${({ image }) => (image ? `url(${image})` : 'transparent')};
        background-size: cover;
        background-position: center;
        border-radius: 2px;
    `
}

const createColumns = ({ eventSlug, onDelete }): ColumnDataGrid[] => [
    {
        name: 'info',
        width: 11,
        render: (performer: PerformerType) => (
            <S.InfoWrapper>
                <S.Avatar>
                    <S.Thumbnail src={performer.mugshot || placeholderImg} alt="" />
                </S.Avatar>
                <S.Title>
                    <S.Name>
                        {performer.name}
                    </S.Name>
                    {performer.about && (
                        <S.About dangerouslySetInnerHTML={{ __html: performer.about }} />
                    )}
                </S.Title>
            </S.InfoWrapper>
        )
    },
    {
        name: 'actions',
        width: 1,
        render: (performer: PerformerType) => (
            <S.Action>
                <Link to={routes.updateSpeaker.getPath({ eventSlug, id: performer.id })}>
                    <Icon icon="edit" size={20} />
                </Link>
                <Icon icon="trash" className="ml-2" size={20} onClick={() => onDelete(performer.id)} />
            </S.Action>
        )
    }
]

type SpeakerDataGridProps = Omit<DataGridProps, 'data'|'columns'> & {
    speakers: PerformerType[]
    fetchStatus: RequestStatus
    onDelete: (id: number) => void
}
const SpeakerDataGrid = ({
    speakers,
    fetchStatus,
    onDelete,
    ...props
}: SpeakerDataGridProps) => {
    const eventSlug = useEventSlug()
    const history = useHistory()
    const columns = useMemo(() => createColumns({ eventSlug, onDelete }), [eventSlug, onDelete])

    if (fetchStatus === 'failed') {
        return <NotFound />
    }

    return (
        <DataGrid
            data={speakers}
            columns={columns}
            hideHeader
            hideAction
            {...props}
            onRowClick={(row) => history.push(routes.updateSpeaker.getPath({ eventSlug, id: row.id }))}
            requestStatus={fetchStatus}
        />
    )
}

const SpeakerList = () => {
    const dispatch = useDispatch()
    const eventSlug = useEventSlug()
    const history = useHistory()
    const [nameFilter, setNameFilter] = useState('')
    const [speakers, setSpeakers] = useState<PerformerType[]>([])
    const [sort, setSort] = useState<PerformerOrder>(PerformerOrder.TitleAsc)

    // remove deleted speaker from the list without calling api to reload
    const removeSpeaker = useCallback((id) => {
        setSpeakers(filter(speakers, (speaker) => speaker.id !== id))
    }, [speakers])
    const { showDeleteConfirmModal } = useDeleteItem(deleteSpeaker, removeSpeaker)

    const {
        list, requestStatus, pagination, totalItems, handlePageChange, fetchData
    } = useSpeakers({ eventId: eventSlug, filters: { name: { iContains: nameFilter } }, sort })

    const sortOptions = useMemo(() => [
        { value: PerformerOrder.TitleAsc, label: _('speaker.list.sort.title_asc') },
        { value: PerformerOrder.TitleDesc, label: _('speaker.list.sort.title_desc') },
    ], [])

    useEffect(() => {
        setSpeakers(list)
    }, [list])

    return (
        <CardWrapper>
            <TopToolbar
                addItemText={_('speaker.list.add_item_button')}
                onAddItem={() => history.push(routes.createSpeaker.getPath({ eventSlug }))}
                onSearch={setNameFilter}
                searchPlaceHolder={_('speaker.list.search_placeholder')}
                sortOptions={sortOptions}
                onSort={setSort}
                bulkActions={{
                    importProps: {
                        ...importProps,
                        onSubmit: (_data) => handleSubmit(eventSlug, _data, dispatch, fetchData),
                    }
                }}
            />
            <SpeakerDataGrid
                speakers={speakers}
                fetchStatus={requestStatus}
                onDelete={showDeleteConfirmModal}
            />
            <Pagination
                page={pagination?.page}
                onPageChange={handlePageChange}
                totalItems={totalItems}
                disabled={requestStatus === 'loading'}
            />
        </CardWrapper>
    )
}

export default SpeakerList
